
import React, { useMemo } from 'react';
import { MakhrajPoint } from '../types';

interface MakhrajViewProps {
  makhraj: MakhrajPoint[];
}

const MakhrajView: React.FC<MakhrajViewProps> = ({ makhraj }) => {
  // Grouping logic sesuai Poster
  const groups = useMemo(() => {
    const grouped = {
      TENGGOROKAN: { color: 'bg-[#008080]', textColor: 'text-white', data: makhraj.filter(m => m.kategori === 'TENGGOROKAN') },
      KERONGKONGAN: { color: 'bg-[#D4AF37]', textColor: 'text-white', data: makhraj.filter(m => m.kategori === 'KERONGKONGAN') },
      LIDAH: { color: 'bg-[#EAB676]', textColor: 'text-[#4A3B2A]', data: makhraj.filter(m => m.kategori === 'LIDAH') },
      BIBIR: { color: 'bg-[#708090]', textColor: 'text-white', data: makhraj.filter(m => m.kategori === 'BIBIR') },
      RONGGA: { color: 'bg-[#B0E0E6]', textColor: 'text-[#005566]', data: makhraj.filter(m => m.kategori === 'RONGGA MULUT') },
      HIDUNG: { color: 'bg-[#A0522D]', textColor: 'text-white', data: makhraj.filter(m => m.kategori === 'HIDUNG') },
    };
    return grouped;
  }, [makhraj]);

  return (
    <div className="bg-[#FDFBF7] p-2 md:p-6 min-h-screen font-sans">
      {/* HEADER POSTER STYLE */}
      <div className="relative mb-6 text-center">
        <div className="absolute inset-0 flex items-center justify-center opacity-10">
           <div className="w-64 h-64 bg-yellow-400 rounded-full blur-3xl"></div>
        </div>
        <h1 className="relative text-2xl md:text-4xl font-black text-[#1a3c3c] uppercase tracking-tighter drop-shadow-sm border-b-4 border-[#008080] inline-block pb-2 px-6">
          MAKHRAJ HURUF HIJA'IYAH
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-6xl mx-auto">
        
        {/* KOLOM KIRI: LEGENDA (Cards) */}
        <div className="lg:col-span-4 space-y-4">
          
          {/* TENGGOROKAN */}
          <MakhrajCard 
            title="TENGGOROKAN" 
            bgColor="bg-[#006666]" 
            items={groups.TENGGOROKAN.data}
            totalHuruf={6}
          />

          {/* KERONGKONGAN (PANGKAL LIDAH) */}
          <MakhrajCard 
            title="KERONGKONGAN" 
            bgColor="bg-[#CC9933]" 
            items={groups.KERONGKONGAN.data}
            totalHuruf={2}
          />

          {/* LIDAH */}
          <MakhrajCard 
            title="LIDAH" 
            bgColor="bg-[#E2A76F]" 
            textColor="text-yellow-900"
            items={groups.LIDAH.data}
            totalHuruf={18} // Visual poster usually sums it up
            cols={3}
          />

           {/* BIBIR */}
           <MakhrajCard 
            title="BIBIR" 
            bgColor="bg-[#6B8E9E]" 
            items={groups.BIBIR.data}
            totalHuruf={4}
          />

           {/* RONGGA MULUT */}
           <MakhrajCard 
            title="RONGGA MULUT" 
            bgColor="bg-[#8FD3E0]" 
            textColor="text-cyan-900"
            items={groups.RONGGA.data}
            totalHuruf={3}
          />

           {/* HIDUNG */}
           <MakhrajCard 
            title="HIDUNG" 
            bgColor="bg-[#8B5A2B]" 
            items={groups.HIDUNG.data}
            footerText="MAKHRAJ DENGUNG"
          />

        </div>

        {/* KOLOM KANAN: ILUSTRASI ANATOMI (SVG) */}
        <div className="lg:col-span-8 bg-[#4A3B2A] rounded-3xl overflow-hidden relative shadow-2xl border-4 border-[#D4AF37]/30 min-h-[600px] flex items-center justify-center">
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#5D4037] to-[#2D241E]"></div>
          
          <div className="relative w-full h-full p-4 transform scale-100 md:scale-110">
            <svg viewBox="0 0 500 600" className="w-full h-full drop-shadow-2xl">
              {/* --- BAGIAN KEPALA & LEHER (Outline & Fill) --- */}
              <defs>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              {/* Leher & Kepala Belakang */}
              <path d="M 400 600 L 400 100 Q 400 50 350 20 L 200 20 Q 100 20 80 150 L 80 250" fill="none" stroke="#E2A76F" strokeWidth="2" opacity="0.5"/>
              
              {/* Wajah Profile (Hidung, Bibir, Dagu) */}
              <path d="M 80 150 Q 70 200 120 220 Q 130 225 120 235 (Hidung)
                       L 120 260 Q 130 265 140 260 (Bibir Atas)
                       L 140 270 Q 130 275 125 285 (Bibir Bawah)
                       L 130 310 Q 150 350 200 350 (Dagu)
                       L 300 350 L 300 600" 
                    fill="#F3E5AB" stroke="#E2A76F" strokeWidth="3" />

              {/* Rongga Mulut & Tenggorokan (Interior) */}
              <path d="M 350 600 L 350 400 Q 350 250 250 250 L 150 250" fill="#FF7F50" opacity="0.9" /> {/* Throat Base */}
              <path d="M 140 260 Q 200 260 220 220 Q 250 150 350 150" fill="none" stroke="#8B0000" strokeWidth="20" strokeLinecap="round" opacity="0.2" /> {/* Nasal cavity hint */}

              {/* Lidah (Tongue) - Besar & Dominan */}
              <path d="M 330 450 Q 280 450 280 350 Q 280 290 200 290 Q 160 290 150 280" 
                    fill="#FF4500" stroke="#8B0000" strokeWidth="2" />

              {/* --- POINTS INDICATOR (Sesuai Gambar 1-17) --- */}
              
              {/* TENGGOROKAN (Blue) 1, 2, 3 */}
              <MakhrajPointSVG x={330} y={550} num={1} color="#008080" label="أ هـ" />
              <MakhrajPointSVG x={330} y={480} num={2} color="#008080" label="ع ح" />
              <MakhrajPointSVG x={330} y={410} num={3} color="#008080" label="غ خ" />

              {/* PANGKAL LIDAH (Yellow) 4, 5 */}
              <MakhrajPointSVG x={300} y={320} num={4} color="#D4AF37" label="ق" />
              <MakhrajPointSVG x={270} y={310} num={5} color="#D4AF37" label="ك" />

              {/* TENGAH LIDAH (Peach) 6 */}
              <MakhrajPointSVG x={230} y={280} num={6} color="#E2A76F" label="ج ش ي" />
              
              {/* SISI LIDAH (Peach) 7 */}
              <MakhrajPointSVG x={240} y={310} num={7} color="#E2A76F" label="ض" />

              {/* UJUNG LIDAH (Variasi) */}
              <MakhrajPointSVG x={200} y={270} num={8} color="#E2A76F" label="ل" size={18} />
              <MakhrajPointSVG x={180} y={270} num={9} color="#E2A76F" label="ن" size={18} />
              <MakhrajPointSVG x={170} y={260} num={10} color="#E2A76F" label="ر" size={18} />

              <MakhrajPointSVG x={160} y={285} num={11} color="#E2A76F" label="ط د ت" textY={20} />
              <MakhrajPointSVG x={150} y={295} num={12} color="#708090" label="م ب و" textY={20} /> {/* Bibir tapi urutan 12 */}
              
              {/* GIGI BAWAH & ATAS */}
              <MakhrajPointSVG x={145} y={310} num={13} color="#708090" label="ف" textY={20} /> {/* Bibir Bawah */}
              <MakhrajPointSVG x={155} y={325} num={14} color="#E2A76F" label="ص س ز" textY={20} /> 
              <MakhrajPointSVG x={165} y={340} num={15} color="#E2A76F" label="ظ ذ ث" textY={20} />

              {/* RONGGA MULUT (Mad) */}
              <MakhrajPointSVG x={220} y={360} num={16} color="#B0E0E6" label="HURUF MAD" size={24} textColor="#000" />
              
              {/* HIDUNG (Ghunnah) */}
              <MakhrajPointSVG x={200} y={200} num={17} color="#A0522D" label="DENGUNG" size={24} />

              {/* Connecting Lines (Imaginary) */}
              <line x1="330" y1="550" x2="380" y2="550" stroke="white" strokeDasharray="4" opacity="0.3" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component for Left Column Cards
const MakhrajCard = ({ title, bgColor, textColor = 'text-white', items, totalHuruf, cols = 0, footerText }: any) => (
  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200">
    <div className={`${bgColor} px-4 py-3 flex justify-between items-center`}>
      <h3 className={`font-black ${textColor} text-lg tracking-wider`}>{title}</h3>
    </div>
    <div className="p-3 bg-[#FFFBF0]">
      <div className={`grid ${cols ? `grid-cols-${cols}` : 'grid-cols-2'} gap-2`}>
        {items.map((m: any) => (
          <div key={m.id} className="flex items-center gap-2 mb-1">
             <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center font-bold ${textColor} text-sm shadow-sm flex-shrink-0`}>
               {m.id}
             </div>
             <div className="flex flex-wrap gap-1">
               {m.huruf.map((h: string, idx: number) => (
                 <span key={idx} className="arabic-font text-2xl font-bold text-slate-800 leading-none">{h}</span>
               ))}
             </div>
          </div>
        ))}
      </div>
    </div>
    <div className={`${bgColor} bg-opacity-20 px-4 py-2 text-center border-t border-slate-100`}>
      <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${bgColor.replace('bg-', 'text-').replace('#', '')} opacity-80 text-slate-600`}>
        {footerText ? footerText : `${totalHuruf} HURUF`}
      </span>
    </div>
  </div>
);

// Sub-component for SVG Points
const MakhrajPointSVG = ({ x, y, num, color, label, size = 20, textY = -15, textColor = "#FFF" }: any) => (
  <g className="cursor-pointer hover:opacity-80 transition-opacity">
    <circle cx={x} cy={y} r={size} fill={color} stroke="white" strokeWidth="2" className="drop-shadow-md" />
    <text x={x} y={y} dy=".3em" textAnchor="middle" fill={textColor} fontSize="12" fontWeight="bold" fontFamily="sans-serif">{num}</text>
    <text x={x} y={y + (textY > 0 ? size + 15 : -size - 5)} textAnchor="middle" fill="#FFF" fontSize="16" fontWeight="bold" className="arabic-font" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
      {label}
    </text>
    {/* Line connector if needed */}
    {textY > 0 && <line x1={x} y1={y + size} x2={x} y2={y + size + 5} stroke="white" strokeWidth="1" />}
  </g>
);

export default MakhrajView;
